import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { APP_STORE_URL, getPlatform } from '../constants/appStore';
import { CELEBRATION } from '../design/motion';
import './SettledCelebration.css';

// The settled confirmation after a payer marks their share as paid, on the public bill and the
// friend settle link. Celebrate first: the check pops in with a ring and a few flecks, confetti
// bursts from it, then the heading and the amount. Pitch second: after a pause Done fades out and
// the App Store invite springs up in its place, one primary action and a quiet way out. The
// settled state is never blocked: Done or Not now closes. Android has no app, so it gets the
// celebration and Done only. Reduced motion: no confetti, and the invite is there from the start.
//
// Nothing reflows: the invite is rendered dormant from the first frame (invisible and inert) so its
// room is part of the layout before the celebration starts, and the hand-over from Done is opacity
// and transform only (#43 no-reflow follow-up).

const FLECKS = 8;
const CONFETTI = 28;
// A deterministic scatter (fractional parts of multiples of the golden ratio) so renders, tests and
// captures agree. Each piece flies out from the check, peaks, then falls past the actions and fades.
// Delays and durations fit inside CELEBRATION.confettiDurationMs, when the layer is removed.
const spread = (i: number, k: number) => ((i + 1) * k * 0.6180339887) % 1;
const PIECES = Array.from({ length: CONFETTI }, (_, i) => {
  const side = i % 2 ? 1 : -1;
  return {
    x: Math.round(side * (24 + spread(i, 1) * 150)),
    peak: -Math.round(30 + spread(i, 2) * 80),
    fall: Math.round(150 + spread(i, 3) * 140),
    rotate: Math.round(side * (240 + spread(i, 4) * 540)),
    delayMs: Math.round(200 + spread(i, 5) * 220),
    durationMs: Math.round(1000 + spread(i, 6) * 180),
    shape: i % 5 === 0 ? 'dot' : 'strip',
    color: i % 4,
  };
});

function reducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function SettledCelebration({ amountLabel, detail, onDone }: { amountLabel?: string; detail?: string; onDone: () => void }) {
  const heading = useRef<HTMLHeadingElement>(null);
  const instant = reducedMotion();
  const canInstall = getPlatform() !== 'android';
  const [confetti, setConfetti] = useState(!instant);
  const [invite, setInvite] = useState(instant);
  // Done stays mounted while it fades, so the invite grows into its place instead of replacing it.
  const [doneGone, setDoneGone] = useState(instant);
  useEffect(() => { heading.current?.focus(); }, []);
  useEffect(() => {
    if (!confetti) return;
    const timer = window.setTimeout(() => setConfetti(false), CELEBRATION.confettiDurationMs);
    return () => window.clearTimeout(timer);
  }, [confetti]);
  useEffect(() => {
    if (invite || !canInstall) return;
    const timer = window.setTimeout(() => setInvite(true), CELEBRATION.inviteDelayMs);
    return () => window.clearTimeout(timer);
  }, [invite, canInstall]);
  useEffect(() => {
    if (!invite || doneGone) return;
    const timer = window.setTimeout(() => setDoneGone(true), CELEBRATION.doneExitDurationMs);
    return () => window.clearTimeout(timer);
  }, [invite, doneGone]);
  const pitch = invite && canInstall;
  const summary = amountLabel ? `${amountLabel} marked as paid.` : 'Marked as paid.';
  return <div className={`settled${instant ? ' settled--instant' : ''}`} data-phase={pitch ? 'invite' : 'celebrate'} data-testid="settled">
    {confetti && <div className="settled-confetti" aria-hidden="true" data-testid="settled-confetti">
      {PIECES.map((piece, i) => <span key={i} className={`settled-confetti-piece settled-confetti-piece--${piece.shape} settled-confetti-piece--${piece.color}`} data-testid="settled-confetti-piece"
        style={{ '--confetti-x': `${piece.x}px`, '--confetti-peak': `${piece.peak}px`, '--confetti-fall': `${piece.fall}px`, '--confetti-rotate': `${piece.rotate}deg`, animationDelay: `${piece.delayMs}ms`, animationDuration: `${piece.durationMs}ms` } as React.CSSProperties}><span /></span>)}
    </div>}
    <div className="settled-mark" aria-hidden="true">
      <span className="settled-ring" />
      {Array.from({ length: FLECKS }, (_, i) => <span key={i} className="settled-fleck" style={{ '--settled-angle': `${(360 / FLECKS) * i}deg` } as React.CSSProperties} />)}
      <span className="settled-check"><Check size={34} strokeWidth={3} aria-hidden="true" /></span>
    </div>
    <h2 ref={heading} tabIndex={-1} className="settled-heading">You’re settled</h2>
    <p className="settled-summary">{summary}{detail ? ` ${detail}` : ''}</p>
    <div className="settled-actions" data-testid="settled-actions">
      {(!pitch || !doneGone) && <div className={`settled-slot${pitch ? ' settled-slot--leaving' : ''}`} data-testid="settled-done-slot">
        <button type="button" className={`vs-button vs-button--primary vs-button--full settled-done${invite ? '' : ' settled-done--waiting'}`} onClick={onDone}>Done</button>
      </div>}
      {canInstall && <div className={`settled-slot ${pitch ? 'settled-slot--arriving' : 'settled-slot--dormant'}`} aria-hidden={!pitch} inert={!pitch} data-testid="settled-invite-slot">
        <div className="settled-invite" role="group" aria-label="Get the app">
          <p className="settled-invite-pitch"><strong>Liked Vibesplit?</strong> Split the next bill in the app.</p>
          <a className="vs-button vs-button--primary vs-button--full" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Download the app</a>
          <button type="button" className="payment-v2-link" onClick={onDone}>Not now</button>
        </div>
      </div>}
    </div>
    <span role="status" className="sr-only">{`You’re settled. ${summary}`}</span>
  </div>;
}
